import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { 
  getCRMClients, createCRMClient, updateCRMClient, deleteCRMClient, addCRMContact, getCRMStats, getFoodTrucks, getEvents, checkEmailLeads,
  getQuotes, getPendingQuotes, generateQuote, validateQuote, rejectQuote, sendQuote, updateQuoteStatus, downloadQuotePdf
} from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Users,
  Plus,
  Search,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  MessageSquare,
  UserCircle,
  Edit,
  Trash2,
  History,
  PhoneCall,
  Video,
  FileText,
  Loader2,
  TrendingUp,
  Target,
  Clock,
  ChevronDown,
  ChevronUp,
  Inbox,
  Sparkles,
  ArrowRight,
  RefreshCw,
  FileCheck,
  Send,
  Download,
  Euro,
  CheckCircle,
  XCircle,
  Truck,
  Upload,
  FileSpreadsheet,
  AlertCircle,
  Save,
  RotateCcw,
  Database,
  Shield,
  CalendarPlus,
  ExternalLink,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import ProposeEventDialog from "../components/ProposeEventDialog";
import PipelineStatut, { BadgeStatut } from "../components/PipelineStatut";
import ListeFiches from "../components/ListeFiches";

const STATUS_LABELS = {
  lead: { label: "Lead", color: "bg-purple-100 text-purple-800" },
  prospect: { label: "Prospect", color: "bg-blue-100 text-blue-800" },
  negociation: { label: "En negociation", color: "bg-amber-100 text-amber-800" },
  valide: { label: "Valide", color: "bg-green-100 text-green-800" },
  refuse: { label: "Refuse", color: "bg-red-100 text-red-800" },
  contacte: { label: "Prospect", color: "bg-blue-100 text-blue-800" },
  client: { label: "Valide", color: "bg-green-100 text-green-800" },
  inactif: { label: "Refuse", color: "bg-red-100 text-red-800" },
};

const TYPE_LABELS = {
  organisateur: "Organisateur d'événements",
  mairie: "Mairie / Collectivité",
  entreprise: "Entreprise",
  association: "Association",
  particulier: "Particulier",
  autre: "Autre",
};

const CONTACT_TYPE_ICONS = {
  appel: PhoneCall,
  email: Mail,
  rdv: Video,
  autre: FileText,
};

export default function CRMPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [foodTrucks, setFoodTrucks] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("clients");
  const [selectedClients, setSelectedClients] = useState([]); // For bulk selection
  const [selectAll, setSelectAll] = useState(false);
  
  // Dialog states
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [expandedClient, setExpandedClient] = useState(null);
  
  // Form states
  const [clientForm, setClientForm] = useState({
    nom_entreprise: "",
    nom_contact: "",
    email: "",
    telephone: "",
    type_client: "autre",
    statut: "prospect",
    point_contact_id: "",
    assigned_food_truck_id: "",
    notes: "",
    adresse: "",
    ville: "",
    evenement_associe_id: "",
  });
  const [contactForm, setContactForm] = useState({
    type_contact: "appel",
    notes: "",
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [checkingEmails, setCheckingEmails] = useState(false);
  
  // Quote states
  const [quotes, setQuotes] = useState([]);
  const [pendingQuotes, setPendingQuotes] = useState([]);
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
    tva_rate: 0,
    notes: "",
  });
  const [selectedClientForQuote, setSelectedClientForQuote] = useState(null);
  const [showNewQuoteDialog, setShowNewQuoteDialog] = useState(false);
  const [newQuoteForm, setNewQuoteForm] = useState({
    nom_entreprise: "",
    nom_contact: "",
    email: "",
    telephone: "",
    adresse: "",
    ville: "",
    items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
    tva_rate: 0,
    notes: "",
  });

  // Import states
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importMapping, setImportMapping] = useState({
    nom_entreprise: "",
    nom_contact: "",
    email: "",
    telephone: "",
    adresse: "",
    ville: "",
    code_postal: "",
    type_client: "",
    notes: "",
  });
  const [availableColumns, setAvailableColumns] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState("prospect"); // Default pipeline status for imported leads
  const [importType, setImportType] = useState("entreprise"); // Default type for imported leads

  // Backup/Restore states
  const [showBackupDialog, setShowBackupDialog] = useState(false);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);

  // Event creation from CRM states
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedClientForEvent, setSelectedClientForEvent] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, statsRes, ftRes, eventsRes, quotesRes, pendingQuotesRes] = await Promise.all([
        getCRMClients(),
        getCRMStats(),
        getFoodTrucks(),
        getEvents(),
        getQuotes().catch(() => ({ data: [] })),
        getPendingQuotes().catch(() => ({ data: [] }))
      ]);
      setClients(clientsRes.data);
      setStats(statsRes.data);
      setFoodTrucks(ftRes.data.filter(ft => ft.membre_bureau));
      setEvents(eventsRes.data);
      setQuotes(quotesRes.data);
      setPendingQuotes(pendingQuotesRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const resetClientForm = () => {
    setClientForm({
      nom_entreprise: "",
      nom_contact: "",
      email: "",
      telephone: "",
      type_client: "autre",
      statut: "prospect",
      point_contact_id: "",
      assigned_food_truck_id: "",
      notes: "",
      adresse: "",
      ville: "",
      evenement_associe_id: "",
    });
  };

  // Bulk selection functions
  const toggleSelectClient = (clientId) => {
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
    setSelectAll(!selectAll);
  };

  const handleBulkDelete = async () => {
    if (selectedClients.length === 0) {
      toast.error("Aucun client sélectionné");
      return;
    }
    
    if (!window.confirm(`Supprimer ${selectedClients.length} client(s) sélectionné(s) du CRM ?`)) return;
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      await axios.post(`${API}/api/crm/clients/bulk-delete`, {
        client_ids: selectedClients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedClients.length} client(s) supprimé(s)`);
      setSelectedClients([]);
      setSelectAll(false);
      loadData();
    } catch (error) {
      console.error("Error bulk deleting:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  // Quote functions
  const openQuoteDialog = (client) => {
    setSelectedClientForQuote(client);
    setQuoteForm({
      items: [{ description: "Prestation Food Truck", quantite: 1, prix_unitaire: 0 }],
      tva_rate: 0,
      notes: "",
    });
    setShowQuoteDialog(true);
  };

  const addQuoteItem = () => {
    setQuoteForm({
      ...quoteForm,
      items: [...quoteForm.items, { description: "", quantite: 1, prix_unitaire: 0 }]
    });
  };

  const removeQuoteItem = (index) => {
    if (quoteForm.items.length > 1) {
      setQuoteForm({
        ...quoteForm,
        items: quoteForm.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateQuoteItem = (index, field, value) => {
    const newItems = [...quoteForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setQuoteForm({ ...quoteForm, items: newItems });
  };

  const calculateQuoteTotal = () => {
    const totalHT = quoteForm.items.reduce((sum, item) => sum + (item.quantite * item.prix_unitaire), 0);
    const tva = totalHT * (quoteForm.tva_rate / 100);
    return { totalHT, tva, totalTTC: totalHT + tva };
  };

  // New Quote functions (without existing client)
  const openNewQuoteDialog = () => {
    setNewQuoteForm({
      nom_entreprise: "",
      nom_contact: "",
      email: "",
      telephone: "",
      adresse: "",
      ville: "",
      items: [{ description: "", quantite: 1, prix_unitaire: 0 }],
      tva_rate: 0,
      notes: "",
    });
    setShowNewQuoteDialog(true);
  };

  const addNewQuoteItem = () => {
    setNewQuoteForm({
      ...newQuoteForm,
      items: [...newQuoteForm.items, { description: "", quantite: 1, prix_unitaire: 0 }]
    });
  };

  const removeNewQuoteItem = (index) => {
    if (newQuoteForm.items.length > 1) {
      setNewQuoteForm({
        ...newQuoteForm,
        items: newQuoteForm.items.filter((_, i) => i !== index)
      });
    }
  };

  const updateNewQuoteItem = (index, field, value) => {
    const newItems = [...newQuoteForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setNewQuoteForm({ ...newQuoteForm, items: newItems });
  };

  const calculateNewQuoteTotal = () => {
    const totalHT = newQuoteForm.items.reduce((sum, item) => sum + (item.quantite * item.prix_unitaire), 0);
    const tva = totalHT * (newQuoteForm.tva_rate / 100);
    return { totalHT, tva, totalTTC: totalHT + tva };
  };

  const handleGenerateNewQuote = async () => {
    if (!newQuoteForm.nom_entreprise) {
      toast.error("Le nom de l'entreprise est requis");
      return;
    }
    
    const validItems = newQuoteForm.items.filter(item => item.description && item.prix_unitaire > 0);
    if (validItems.length === 0) {
      toast.error("Ajoutez au moins une prestation avec un prix");
      return;
    }

    setSubmitting(true);
    try {
      // First create a client
      const clientResponse = await createCRMClient({
        nom_entreprise: newQuoteForm.nom_entreprise,
        nom_contact: newQuoteForm.nom_contact,
        email: newQuoteForm.email,
        telephone: newQuoteForm.telephone,
        adresse: newQuoteForm.adresse,
        ville: newQuoteForm.ville,
        statut: "prospect",
        type_client: "entreprise",
      });
      
      const newClientId = clientResponse.data.client?.id || clientResponse.data.id;
      
      // Then create the quote
      await generateQuote({
        client_id: newClientId,
        items: validItems,
        tva_rate: newQuoteForm.tva_rate,
        notes: newQuoteForm.notes,
      });
      
      toast.success("Client créé et devis généré avec succès !");
      setShowNewQuoteDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création du devis");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateQuote = async () => {
    if (!selectedClientForQuote) return;
    
    const validItems = quoteForm.items.filter(item => item.description && item.prix_unitaire > 0);
    if (validItems.length === 0) {
      toast.error("Ajoutez au moins une prestation avec un prix");
      return;
    }

    setSubmitting(true);
    try {
      const response = await generateQuote({
        client_id: selectedClientForQuote.id,
        items: validItems,
        tva_rate: quoteForm.tva_rate,
        notes: quoteForm.notes,
      });
      
      toast.success("Devis généré ! En attente de validation.");
      setShowQuoteDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la génération du devis");
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidateQuote = async (quoteId) => {
    try {
      await validateQuote(quoteId);
      toast.success("Devis validé !");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la validation");
    }
  };

  const handleRejectQuote = async (quoteId) => {
    try {
      await rejectQuote(quoteId);
      toast.success("Devis refusé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors du refus");
    }
  };

  const handleSendQuote = async (quoteId) => {
    try {
      const response = await sendQuote(quoteId);
      toast.success(response.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  const handleDownloadQuote = async (quoteId, numero) => {
    try {
      const response = await downloadQuotePdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_${numero}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleUpdateQuoteStatus = async (quoteId, status) => {
    try {
      await updateQuoteStatus(quoteId, status);
      toast.success(`Statut mis à jour: ${status}`);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const QUOTE_STATUS = {
    brouillon: { label: "Brouillon", color: "bg-gray-100 text-gray-800" },
    en_attente_validation: { label: "À valider", color: "bg-yellow-100 text-yellow-800" },
    valide: { label: "Validé", color: "bg-blue-100 text-blue-800" },
    envoye: { label: "Envoyé", color: "bg-indigo-100 text-indigo-800" },
    accepte: { label: "Accepté", color: "bg-green-100 text-green-800" },
    refuse: { label: "Refusé", color: "bg-red-100 text-red-800" },
    expire: { label: "Expiré", color: "bg-gray-100 text-gray-500" },
  };

  const openNewClientDialog = () => {
    setEditingClient(null);
    resetClientForm();
    setShowClientDialog(true);
  };

  // Import functions
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportFile(file);
    
    // Send file to backend for parsing
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/api/crm/import/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de l'analyse du fichier");
      }
      
      const data = await response.json();
      setAvailableColumns(data.columns);
      setImportPreview(data.preview);
      
      // Auto-map columns based on common names
      const autoMapping = {};
      const columnLower = data.columns.map(c => c.toLowerCase());
      
      if (columnLower.includes("nom") || columnLower.includes("nom_entreprise") || columnLower.includes("societe") || columnLower.includes("name")) {
        autoMapping.nom_entreprise = data.columns[columnLower.findIndex(c => c === "nom" || c === "nom_entreprise" || c === "societe" || c === "name")];
      }
      if (columnLower.includes("contact") || columnLower.includes("nom_contact")) {
        autoMapping.nom_contact = data.columns[columnLower.findIndex(c => c === "contact" || c === "nom_contact")];
      }
      if (columnLower.includes("email") || columnLower.includes("mail") || columnLower.includes("adresse e-mail")) {
        autoMapping.email = data.columns[columnLower.findIndex(c => c === "email" || c === "mail" || c === "adresse e-mail")];
      }
      if (columnLower.includes("telephone") || columnLower.includes("tel") || columnLower.includes("phone")) {
        autoMapping.telephone = data.columns[columnLower.findIndex(c => c === "telephone" || c === "tel" || c === "phone")];
      }
      if (columnLower.includes("adresse") || columnLower.includes("address")) {
        autoMapping.adresse = data.columns[columnLower.findIndex(c => c === "adresse" || c === "address")];
      }
      if (columnLower.includes("ville") || columnLower.includes("city")) {
        autoMapping.ville = data.columns[columnLower.findIndex(c => c === "ville" || c === "city")];
      }
      if (columnLower.includes("code postal") || columnLower.includes("code_postal") || columnLower.includes("cp")) {
        autoMapping.code_postal = data.columns[columnLower.findIndex(c => c === "code postal" || c === "code_postal" || c === "cp")];
      }
      if (columnLower.includes("activite") || columnLower.includes("type") || columnLower.includes("activity")) {
        autoMapping.notes = data.columns[columnLower.findIndex(c => c === "activite" || c === "type" || c === "activity")];
      }
      
      setImportMapping(prev => ({ ...prev, ...autoMapping }));
      toast.success(`${data.total_rows} lignes trouvées dans le fichier`);
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'analyse du fichier");
      setImportFile(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error("Veuillez sélectionner un fichier");
      return;
    }
    
    if (!importMapping.nom_entreprise || importMapping.nom_entreprise === "__none__") {
      toast.error("Veuillez mapper au moins le champ 'Nom entreprise'");
      return;
    }
    
    setImporting(true);
    
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", importFile);
      
      // Clean mapping - remove __none__ values
      const cleanMapping = {};
      Object.entries(importMapping).forEach(([key, value]) => {
        if (value && value !== "__none__") {
          cleanMapping[key] = value;
        }
      });
      
      formData.append("mapping", JSON.stringify(cleanMapping));
      formData.append("default_status", importStatus);
      formData.append("default_type", importType);
      
      const response = await fetch(`${API}/api/crm/import`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Erreur lors de l'import");
      }
      
      const data = await response.json();
      toast.success(`${data.imported} leads importés avec succès ! ${data.skipped} ignorés (doublons)`);
      setShowImportDialog(false);
      setImportFile(null);
      setImportPreview([]);
      setAvailableColumns([]);
      setImportMapping({
        nom_entreprise: "",
        nom_contact: "",
        email: "",
        telephone: "",
        adresse: "",
        ville: "",
        code_postal: "",
        type_client: "",
        notes: "",
      });
      loadData();
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'import");
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportPreview([]);
    setAvailableColumns([]);
    setImportMapping({
      nom_entreprise: "",
      nom_contact: "",
      email: "",
      telephone: "",
      adresse: "",
      ville: "",
      code_postal: "",
      type_client: "",
      notes: "",
    });
  };

  // ==================== BACKUP & RESTORE ====================
  const API = process.env.REACT_APP_BACKEND_URL;

  const loadBackups = async () => {
    setLoadingBackups(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/crm/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des backups");
    } finally {
      setLoadingBackups(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/api/crm/backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ ${response.data.message}`);
      loadBackups();
    } catch (error) {
      toast.error("Erreur lors de la création du backup");
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestoreBackup = async (backupId, backupDate) => {
    if (!window.confirm(`⚠️ ATTENTION: Cette action va REMPLACER tous vos clients/leads actuels par ceux du backup du ${backupDate}.\n\nUn backup de sécurité sera créé automatiquement.\n\nContinuer ?`)) {
      return;
    }
    
    setRestoringBackup(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/api/crm/restore/${backupId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ ${response.data.message}`);
      setShowBackupDialog(false);
      loadData(); // Reload all data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la restauration");
    } finally {
      setRestoringBackup(false);
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/crm/backup/${backupId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_backup_${backupId}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Backup téléchargé");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const openBackupDialog = () => {
    setShowBackupDialog(true);
    loadBackups();
  };

  // Open event creation dialog with client data pre-filled
  const openEventDialogFromClient = (client) => {
    setSelectedClientForEvent(client);
    setShowEventDialog(true);
  };

  // Handle successful event creation - link it to the client
  const handleEventCreatedFromCRM = async (eventData) => {
    // Update client with the new event ID
    if (selectedClientForEvent && eventData?.id) {
      try {
        await updateCRMClient(selectedClientForEvent.id, {
          ...selectedClientForEvent,
          evenement_associe_id: eventData.id,
          statut: selectedClientForEvent.statut === 'lead' ? 'contacte' : selectedClientForEvent.statut
        });
        toast.success("Client associé à l'événement");
        loadData();
      } catch (error) {
        console.error("Error linking event to client:", error);
      }
    }
    setShowEventDialog(false);
    setSelectedClientForEvent(null);
  };

  const openEditClientDialog = (client) => {
    setEditingClient(client);
    setClientForm({
      nom_entreprise: client.nom_entreprise,
      nom_contact: client.nom_contact,
      email: client.email || "",
      telephone: client.telephone || "",
      type_client: client.type_client,
      statut: client.statut,
      point_contact_id: client.point_contact_id || "",
      assigned_food_truck_id: client.assigned_food_truck_id || "",
      notes: client.notes || "",
      adresse: client.adresse || "",
      ville: client.ville || "",
      evenement_associe_id: client.evenement_associe_id || "",
    });
    setShowClientDialog(true);
  };

  const handleSubmitClient = async () => {
    if (!clientForm.nom_entreprise || !clientForm.nom_contact) {
      toast.error("Nom de l'entreprise et du contact requis");
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingClient) {
        await updateCRMClient(editingClient.id, clientForm);
        toast.success("Client mis à jour");
      } else {
        await createCRMClient(clientForm);
        toast.success("Client ajouté");
      }
      setShowClientDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm("Supprimer ce client ?")) return;
    
    try {
      await deleteCRMClient(clientId);
      toast.success("Client supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCheckEmails = async () => {
    setCheckingEmails(true);
    try {
      const response = await checkEmailLeads();
      const leadsCreated = response.data.leads_created;
      if (leadsCreated > 0) {
        toast.success(`${leadsCreated} nouveau(x) lead(s) récupéré(s) !`);
        loadData();
      } else {
        toast.info("Aucun nouveau lead dans la boîte email");
      }
    } catch (error) {
      toast.error("Erreur lors de la vérification des emails");
    } finally {
      setCheckingEmails(false);
    }
  };

  const openContactDialog = (client) => {
    setSelectedClient(client);
    setContactForm({ type_contact: "appel", notes: "" });
    setShowContactDialog(true);
  };

  const handleAddContact = async () => {
    if (!contactForm.notes) {
      toast.error("Veuillez ajouter des notes");
      return;
    }
    
    setSubmitting(true);
    try {
      await addCRMContact(selectedClient.id, contactForm);
      toast.success("Contact ajouté");
      setShowContactDialog(false);
      loadData();
    } catch (error) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  const openHistoryDialog = (client) => {
    setSelectedClient(client);
    setShowHistoryDialog(true);
  };

  // Convert lead to prospect
  const convertLeadToProspect = async (client) => {
    try {
      await updateCRMClient(client.id, { statut: "prospect" });
      toast.success("Lead converti en prospect");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la conversion");
    }
  };

  // Get new leads count
  const newLeadsCount = clients.filter(c => c.statut === "lead").length;

  const filteredClients = clients.filter(client => {
    const matchesSearch = 
      client.nom_entreprise.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.nom_contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.ville?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || client.statut === statusFilter;
    const matchesType = typeFilter === "all" || client.type_client === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const HORS_ZONE = ["negociation", "valide", "refuse"];
  const clientsActifs = filteredClients.filter(c => !HORS_ZONE.includes(c.statut));
  const clientsNego = filteredClients.filter(c => c.statut === "negociation");
  const clientsValides = filteredClients.filter(c => c.statut === "valide");

  const changerStatut = async (client, statut) => {
    try {
      await axios.put(`${API}/api/crm/clients/${client.id}`, { statut });
      setClients(prev => prev.map(c => (c.id === client.id ? { ...c, statut } : c)));
      toast.success("Statut mis a jour");
    } catch (e) {
      toast.error("Changement de statut impossible");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-primary flex items-center gap-3">
            <Users className="w-8 h-8" />
            CRM - Clients
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des contacts et prospects
            {selectedClients.length > 0 && (
              <span className="ml-2 text-primary font-medium">
                ({selectedClients.length} sélectionné{selectedClients.length > 1 ? "s" : ""})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {/* Bulk delete button - only show when items are selected */}
          {selectedClients.length > 0 && (
            <Button variant="destructive" onClick={handleBulkDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer ({selectedClients.length})
            </Button>
          )}
          <Button onClick={openNewClientDialog} className="bg-primary hover:bg-primary-hover">
            <Plus className="w-4 h-4 mr-2" />
            Nouveau client
          </Button>
          <Button 
            onClick={openNewQuoteDialog} 
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Euro className="w-4 h-4 mr-2" />
            Nouveau devis
          </Button>
          <Button 
            onClick={() => setShowImportDialog(true)} 
            variant="outline" 
            className="border-green-300 text-green-700 hover:bg-green-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer
          </Button>
          <Button 
            onClick={openBackupDialog} 
            variant="outline" 
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Database className="w-4 h-4 mr-2" />
            Backup / Restaurer
          </Button>
        </div>
      </div>

      {/* Tabs for Leads vs Clients */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 h-auto gap-1">
          <TabsTrigger value="leads" className="flex items-center gap-2">
            <Inbox className="w-4 h-4" />
            Arrivée des leads
            {newLeadsCount > 0 && (
              <Badge className="bg-purple-500 text-white ml-1">{newLeadsCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Tous les clients
          </TabsTrigger>
          <TabsTrigger value="negociation" className="flex items-center gap-2">
            <Target className="w-4 h-4" /> En negociation ({clientsNego.length})
          </TabsTrigger>
          <TabsTrigger value="valide" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Valides ({clientsValides.length})
          </TabsTrigger>
          <TabsTrigger value="devis" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Devis
            {pendingQuotes.length > 0 && (
              <Badge className="ml-1 bg-yellow-500 text-white text-xs">{pendingQuotes.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Tab: Arrivée des leads */}
        <TabsContent value="leads" className="space-y-6 mt-6">
          {/* Info box about automatic leads */}
          <Card className="bg-gradient-to-r from-purple-50 to-white border-purple-200">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-purple-700">Leads par email</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Les emails reçus sur <strong>lead@foodtruckunited.fr</strong> sont automatiquement convertis en leads.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Vérification automatique toutes les 5 minutes.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleCheckEmails}
                  disabled={checkingEmails}
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  {checkingEmails ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Vérifier maintenant
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* New Leads List */}
          <Card className="bg-white border-[#E5E0DC]">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <Inbox className="w-5 h-5 text-purple-600" />
                Nouveaux leads à traiter ({newLeadsCount})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clients.filter(c => c.statut === "lead").length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Inbox className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>Aucun nouveau lead</p>
                  <p className="text-sm">Les leads de votre formulaire apparaîtront ici automatiquement</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {clients.filter(c => c.statut === "lead").map((client) => (
                    <div
                      key={client.id}
                      className="border border-purple-200 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                    >
                      {/* Email Subject Header - if from email */}
                      {client.email_subject && (
                        <div className="bg-gradient-to-r from-purple-600 to-purple-500 px-4 py-3">
                          <div className="flex items-center gap-2 text-white">
                            <Mail className="w-4 h-4" />
                            <span className="text-sm font-medium">Objet :</span>
                            <span className="text-sm">{client.email_subject}</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="p-5">
                        <div className="flex flex-col gap-4">
                          {/* Header */}
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                <span className="text-purple-600 font-bold text-lg">
                                  {client.nom_contact?.charAt(0)?.toUpperCase() || "?"}
                                </span>
                              </div>
                              <div>
                                <h3 className="font-heading text-lg font-bold text-secondary">
                                  {client.nom_contact}
                                </h3>
                                {client.nom_entreprise && client.nom_entreprise !== "Non renseigné" && (
                                  <p className="text-sm text-muted-foreground">{client.nom_entreprise}</p>
                                )}
                              </div>
                              <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                                Nouveau lead
                              </Badge>
                              {client.created_by === "email_automatique" && (
                                <Badge variant="outline" className="border-blue-300 text-blue-600 bg-blue-50">
                                  <Mail className="w-3 h-3 mr-1" />
                                  Via email
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={() => openEventDialogFromClient(client)}
                                variant="outline"
                                className="border-primary text-primary hover:bg-primary hover:text-white"
                              >
                                <CalendarPlus className="w-4 h-4 mr-1" />
                                Créer événement
                              </Button>
                              <Button
                                onClick={() => convertLeadToProspect(client)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <ArrowRight className="w-4 h-4 mr-1" />
                                Convertir en prospect
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditClientDialog(client)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteClient(client.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Contact Info Cards */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {client.email && (
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                  <Mail className="w-4 h-4 text-purple-600" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs text-muted-foreground">Email</p>
                                  <a href={`mailto:${client.email}`} className="text-sm font-medium hover:text-primary truncate block">{client.email}</a>
                                </div>
                              </div>
                            )}
                            {client.telephone && (
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="p-2 bg-green-100 rounded-lg">
                                  <Phone className="w-4 h-4 text-green-600" />
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Téléphone</p>
                                  <a href={`tel:${client.telephone}`} className="text-sm font-medium hover:text-primary">{client.telephone}</a>
                                </div>
                              </div>
                            )}
                            {client.date_evenement && (
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="p-2 bg-orange-100 rounded-lg">
                                  <Calendar className="w-4 h-4 text-orange-600" />
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Date événement</p>
                                  <p className="text-sm font-medium">{client.date_evenement}</p>
                                </div>
                              </div>
                            )}
                            {client.nombre_participants && (
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <Users className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Participants</p>
                                  <p className="text-sm font-medium">{client.nombre_participants} personnes</p>
                                </div>
                              </div>
                            )}
                            {client.specialites && (
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="p-2 bg-yellow-100 rounded-lg">
                                  <Sparkles className="w-4 h-4 text-yellow-600" />
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Spécialités souhaitées</p>
                                  <p className="text-sm font-medium">
                                    {Array.isArray(client.specialites) ? client.specialites.join(", ") : client.specialites}
                                  </p>
                                </div>
                              </div>
                            )}
                            {client.budget && (
                              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                <div className="p-2 bg-emerald-100 rounded-lg">
                                  <Target className="w-4 h-4 text-emerald-600" />
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Budget</p>
                                  <p className="text-sm font-medium">{client.budget}</p>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Assigned Food Truck */}
                          {client.assigned_food_truck_name && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                              <Truck className="w-5 h-5 text-green-600" />
                              <span className="text-sm font-medium text-green-800">Pris en charge par :</span>
                              <Badge className="bg-green-600 text-white">
                                {client.assigned_food_truck_name}
                              </Badge>
                            </div>
                          )}

                          {/* Message Content - Better formatted */}
                          {client.notes && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                              <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
                                <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  {client.created_by === "email_automatique" ? "Contenu du message" : "Notes"}
                                </p>
                              </div>
                              <div className="p-4 bg-white max-h-64 overflow-y-auto">
                                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">
                                  {client.notes}
                                </pre>
                              </div>
                            </div>
                          )}

                          {/* Timestamp */}
                          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t border-gray-100">
                            <span>
                              Reçu le {format(parseISO(client.created_at), "EEEE dd MMMM yyyy 'à' HH:mm", { locale: fr })}
                            </span>
                            {client.created_by === "email_automatique" && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <Mail className="w-3 h-3" />
                                Importé automatiquement
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Tous les clients */}
        <TabsContent value="clients" className="space-y-6 mt-6">
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{stats.total_clients}</p>
                <p className="text-xs text-muted-foreground">Total clients</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Target className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{stats.by_status?.prospect || 0}</p>
                <p className="text-xs text-muted-foreground">Prospects</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <PhoneCall className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{stats.by_status?.negociation || 0}</p>
                <p className="text-xs text-muted-foreground">En négociation</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{stats.by_status?.valide || 0}</p>
                <p className="text-xs text-muted-foreground">Validés</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#E5E0DC]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-accent/10 rounded-lg">
                <Clock className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold text-secondary">{stats.recent_contacts_30d}</p>
                <p className="text-xs text-muted-foreground">Contacts (30j)</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher par nom, email, ville..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 border-[#E5E0DC]"
                data-testid="search-clients"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] border-[#E5E0DC]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[200px] border-[#E5E0DC]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Clients List */}
      <Card className="bg-white border-[#E5E0DC]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-xl text-secondary">
              Clients ({clientsActifs.length})
            </CardTitle>
            {clientsActifs.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleSelectAll}
                className="text-sm"
              >
                {selectAll ? "Tout désélectionner" : "Tout sélectionner"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {clientsActifs.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-secondary">Aucun client</h3>
              <p className="text-muted-foreground">
                {searchTerm || statusFilter !== "all" || typeFilter !== "all" 
                  ? "Aucun résultat pour ces filtres" 
                  : "Ajoutez votre premier client"}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {<ListeFiches liste={clientsActifs} vide="Aucun client en premier contact."
              onHistorique={openHistoryDialog} onEdit={openEditClientDialog}
              onStatut={changerStatut} />}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Tab: Devis */}
        <TabsContent value="negociation" className="space-y-4 mt-6">
          <ListeFiches liste={clientsNego} vide="Aucune affaire en negociation."
            onHistorique={openHistoryDialog} onEdit={openEditClientDialog} onStatut={changerStatut} />
        </TabsContent>

        <TabsContent value="valide" className="space-y-4 mt-6">
          <ListeFiches liste={clientsValides} vide="Aucune affaire validee."
            onHistorique={openHistoryDialog} onEdit={openEditClientDialog} onStatut={changerStatut} />
        </TabsContent>

        <TabsContent value="devis" className="space-y-6 mt-6">
          {/* Pending quotes alert */}
          {pendingQuotes.length > 0 && (
            <Card className="bg-gradient-to-r from-yellow-50 to-white border-yellow-300">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <FileCheck className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-yellow-700">
                      {pendingQuotes.length} devis en attente de validation
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Validez les devis pour pouvoir les envoyer aux clients
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quotes list */}
          <Card className="border-[#E5E0DC]">
            <CardHeader className="bg-gradient-to-r from-[#F9F7F5] to-white border-b border-[#E5E0DC]">
              <CardTitle className="font-heading text-xl text-secondary flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Tous les devis
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {quotes.length === 0 ? (
                <div className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-muted-foreground">Aucun devis créé</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Générez un devis depuis la fiche d'un client
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Devis</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Montant TTC</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map((quote) => {
                      const statusInfo = QUOTE_STATUS[quote.statut] || QUOTE_STATUS.brouillon;
                      return (
                        <TableRow key={quote.id}>
                          <TableCell className="font-medium">{quote.numero}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{quote.client_nom}</p>
                              {quote.client_email && (
                                <p className="text-xs text-muted-foreground">{quote.client_email}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold text-primary">
                            {quote.total_ttc?.toFixed(2)} €
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {quote.date_emission}
                          </TableCell>
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadQuote(quote.id, quote.numero)}
                                title="Télécharger PDF"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              
                              {quote.statut === "en_attente_validation" && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleValidateQuote(quote.id)}
                                    className="text-green-600 hover:text-green-700"
                                    title="Valider"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRejectQuote(quote.id)}
                                    className="text-red-600 hover:text-red-700"
                                    title="Refuser"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              
                              {quote.statut === "valide" && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSendQuote(quote.id)}
                                  className="text-indigo-600 border-indigo-300"
                                  title="Envoyer au client"
                                >
                                  <Send className="w-4 h-4 mr-1" />
                                  Envoyer
                                </Button>
                              )}
                              
                              {quote.statut === "envoye" && (
                                <Select
                                  value=""
                                  onValueChange={(value) => handleUpdateQuoteStatus(quote.id, value)}
                                >
                                  <SelectTrigger className="w-28 h-8">
                                    <SelectValue placeholder="Marquer..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="accepte">Accepté</SelectItem>
                                    <SelectItem value="refuse">Refusé</SelectItem>
                                    <SelectItem value="expire">Expiré</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Client Dialog */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">
              {editingClient ? "Modifier le client" : "Nouveau client"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-secondary font-semibold">Nom de l'entreprise *</Label>
              <Input
                value={clientForm.nom_entreprise}
                onChange={(e) => setClientForm({...clientForm, nom_entreprise: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Ex: Mairie de Paris"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Nom du contact *</Label>
              <Input
                value={clientForm.nom_contact}
                onChange={(e) => setClientForm({...clientForm, nom_contact: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Ex: Jean Dupont"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Email</Label>
              <Input
                type="email"
                value={clientForm.email}
                onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="contact@example.com"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Téléphone</Label>
              <Input
                value={clientForm.telephone}
                onChange={(e) => setClientForm({...clientForm, telephone: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="01 23 45 67 89"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Type de client</Label>
              <Select value={clientForm.type_client} onValueChange={(v) => setClientForm({...clientForm, type_client: v})}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Statut</Label>
              <Select value={clientForm.statut} onValueChange={(v) => setClientForm({...clientForm, statut: v})}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Point de contact FTU</Label>
              <Select value={clientForm.point_contact_id || "none"} onValueChange={(v) => setClientForm({...clientForm, point_contact_id: v === "none" ? "" : v})}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]">
                  <SelectValue placeholder="Sélectionner un membre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {foodTrucks.map((ft) => (
                    <SelectItem key={ft.id} value={ft.id}>{ft.nom_food_truck}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-secondary font-semibold flex items-center gap-2">
                <Truck className="w-4 h-4 text-green-600" />
                Food truck en charge du dossier
              </Label>
              <Select value={clientForm.assigned_food_truck_id || "none"} onValueChange={(v) => setClientForm({...clientForm, assigned_food_truck_id: v === "none" ? "" : v})}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]">
                  <SelectValue placeholder="Sélectionner un food truck" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non assigné</SelectItem>
                  {foodTrucks.map((ft) => (
                    <SelectItem key={ft.id} value={ft.id}>{ft.nom_food_truck}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Adresse</Label>
              <Input
                value={clientForm.adresse}
                onChange={(e) => setClientForm({...clientForm, adresse: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="123 rue Example"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Ville</Label>
              <Input
                value={clientForm.ville}
                onChange={(e) => setClientForm({...clientForm, ville: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Paris"
              />
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Événement associé</Label>
              <Select value={clientForm.evenement_associe_id || "none"} onValueChange={(v) => setClientForm({...clientForm, evenement_associe_id: v === "none" ? "" : v})}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {events.map((event) => (
                    <SelectItem key={event.id} value={event.id}>{event.titre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="md:col-span-2">
              <Label className="text-secondary font-semibold">Notes</Label>
              <Textarea
                value={clientForm.notes}
                onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Informations supplémentaires..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClientDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmitClient} disabled={submitting} className="bg-primary hover:bg-primary-hover">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingClient ? "Modifier" : "Créer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Contact Dialog */}
      <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">
              Ajouter un contact
            </DialogTitle>
            <DialogDescription>
              {selectedClient?.nom_entreprise}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-secondary font-semibold">Type de contact</Label>
              <Select value={contactForm.type_contact} onValueChange={(v) => setContactForm({...contactForm, type_contact: v})}>
                <SelectTrigger className="mt-1 border-[#E5E0DC]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appel">Appel téléphonique</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="rdv">Rendez-vous</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-secondary font-semibold">Notes *</Label>
              <Textarea
                value={contactForm.notes}
                onChange={(e) => setContactForm({...contactForm, notes: e.target.value})}
                className="mt-1 border-[#E5E0DC]"
                placeholder="Résumé de l'échange..."
                rows={4}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddContact} disabled={submitting} className="bg-primary hover:bg-primary-hover">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">
              Historique des contacts
            </DialogTitle>
            <DialogDescription>
              {selectedClient?.nom_entreprise}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {selectedClient?.historique_contacts?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Aucun contact enregistré</p>
              </div>
            ) : (
              selectedClient?.historique_contacts?.slice().reverse().map((contact) => {
                const Icon = CONTACT_TYPE_ICONS[contact.type_contact] || FileText;
                return (
                  <div key={contact.id} className="border border-[#E5E0DC] rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-primary" />
                        <span className="font-medium text-secondary capitalize">
                          {contact.type_contact === "rdv" ? "Rendez-vous" : contact.type_contact}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {format(parseISO(contact.date), "dd/MM/yyyy HH:mm", { locale: fr })}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{contact.notes}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Par: {contact.created_by_name}
                    </p>
                  </div>
                );
              })
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              Fermer
            </Button>
            <Button onClick={() => { setShowHistoryDialog(false); openContactDialog(selectedClient); }} className="bg-primary hover:bg-primary-hover">
              <Plus className="w-4 h-4 mr-1" /> Ajouter un contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Dialog */}
      <Dialog open={showQuoteDialog} onOpenChange={setShowQuoteDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">
              Générer un devis
            </DialogTitle>
            <DialogDescription>
              Pour : {selectedClientForQuote?.nom_entreprise}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Client info summary */}
            <div className="p-4 bg-[#F9F7F5] rounded-lg">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Contact :</span>
                  <span className="ml-2 font-medium">{selectedClientForQuote?.nom_contact}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Email :</span>
                  <span className="ml-2">{selectedClientForQuote?.email || "Non renseigné"}</span>
                </div>
                {selectedClientForQuote?.date_evenement && (
                  <div>
                    <span className="text-muted-foreground">Date événement :</span>
                    <span className="ml-2">{selectedClientForQuote.date_evenement}</span>
                  </div>
                )}
                {selectedClientForQuote?.nombre_participants && (
                  <div>
                    <span className="text-muted-foreground">Participants :</span>
                    <span className="ml-2">{selectedClientForQuote.nombre_participants}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Prestations */}
            <div>
              <Label className="text-secondary font-semibold mb-3 block">Prestations</Label>
              <div className="space-y-3">
                {quoteForm.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border border-[#E5E0DC] rounded-lg">
                    <Input
                      placeholder="Description de la prestation"
                      value={item.description}
                      onChange={(e) => updateQuoteItem(index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="text"
                      inputMode="numeric"
                      placeholder="Qté"
                      value={item.quantite}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateQuoteItem(index, 'quantite', val === "" ? 1 : parseInt(val) || 1);
                      }}
                      className="w-20"
                    />
                    <Input
                      type="number"
                      inputMode="decimal"
                      placeholder="Prix €"
                      value={item.prix_unitaire || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateQuoteItem(index, 'prix_unitaire', val === "" ? 0 : parseFloat(val) || 0);
                      }}
                      className="w-28"
                    />
                    {quoteForm.items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuoteItem(index)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addQuoteItem}
                  className="w-full border-dashed"
                >
                  <Plus className="w-4 h-4 mr-1" /> Ajouter une prestation
                </Button>
              </div>
            </div>

            {/* TVA */}
            <div>
              <Label className="text-secondary font-semibold">TVA</Label>
              <Select
                value={quoteForm.tva_rate.toString()}
                onValueChange={(value) => setQuoteForm({...quoteForm, tva_rate: parseFloat(value)})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Non applicable (art. 293B)</SelectItem>
                  <SelectItem value="10">10%</SelectItem>
                  <SelectItem value="20">20%</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-secondary font-semibold">Notes (optionnel)</Label>
              <Textarea
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})}
                className="mt-1"
                placeholder="Informations supplémentaires..."
                rows={3}
              />
            </div>

            {/* Total */}
            <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total HT</span>
                  <span className="font-medium">{calculateQuoteTotal().totalHT.toFixed(2)} €</span>
                </div>
                {quoteForm.tva_rate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>TVA ({quoteForm.tva_rate}%)</span>
                    <span>{calculateQuoteTotal().tva.toFixed(2)} €</span>
                  </div>
                )}
                <div className="flex justify-between text-lg font-bold text-primary pt-2 border-t">
                  <span>Total TTC</span>
                  <span>{calculateQuoteTotal().totalTTC.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleGenerateQuote} 
              disabled={submitting}
              className="bg-primary hover:bg-primary-hover"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Génération...</>
              ) : (
                <><FileText className="w-4 h-4 mr-2" /> Générer le devis</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => { 
        setShowImportDialog(open); 
        if (!open) resetImport();
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              Importer des leads
            </DialogTitle>
            <DialogDescription>
              Importez des contacts depuis un fichier Excel (.xlsx) ou CSV
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* File Upload */}
            {!importFile && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-400 transition-colors">
                <FileSpreadsheet className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium mb-2">Glissez-déposez votre fichier ici</p>
                <p className="text-sm text-muted-foreground mb-4">ou</p>
                <label className="cursor-pointer inline-block">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2">
                    <Upload className="w-4 h-4" />
                    Sélectionner un fichier
                  </div>
                </label>
                <p className="text-xs text-muted-foreground mt-4">
                  Formats acceptés : CSV, Excel (.xlsx, .xls)
                </p>
              </div>
            )}
            
            {/* File selected - show mapping */}
            {importFile && availableColumns.length > 0 && (
              <>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-800">{importFile.name}</p>
                      <p className="text-sm text-green-600">{importPreview.length > 0 ? "Aperçu disponible" : "Fichier chargé"}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={resetImport}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <XCircle className="w-4 h-4" />
                  </Button>
                </div>

                {/* Default settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="text-secondary font-semibold">Statut par défaut</Label>
                    <Select value={importStatus} onValueChange={setImportStatus}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lead">Nouveau lead</SelectItem>
                        <SelectItem value="prospect">Prospect</SelectItem>
                        <SelectItem value="contacte">Contacté</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-secondary font-semibold">Type par défaut</Label>
                    <Select value={importType} onValueChange={setImportType}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="entreprise">Entreprise</SelectItem>
                        <SelectItem value="mairie">Mairie / Collectivité</SelectItem>
                        <SelectItem value="association">Association</SelectItem>
                        <SelectItem value="particulier">Particulier</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Column mapping */}
                <div>
                  <h3 className="font-semibold text-secondary mb-3 flex items-center gap-2">
                    <ArrowRight className="w-4 h-4" />
                    Correspondance des colonnes
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Associez les colonnes de votre fichier aux champs du CRM
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Nom entreprise <span className="text-red-500">*</span></Label>
                      <Select 
                        value={importMapping.nom_entreprise} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, nom_entreprise: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Nom contact</Label>
                      <Select 
                        value={importMapping.nom_contact} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, nom_contact: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Email</Label>
                      <Select 
                        value={importMapping.email} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, email: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Téléphone</Label>
                      <Select 
                        value={importMapping.telephone} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, telephone: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Adresse</Label>
                      <Select 
                        value={importMapping.adresse} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, adresse: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Ville</Label>
                      <Select 
                        value={importMapping.ville} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, ville: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Code postal</Label>
                      <Select 
                        value={importMapping.code_postal} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, code_postal: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-sm">Notes / Activité</Label>
                      <Select 
                        value={importMapping.notes} 
                        onValueChange={(v) => setImportMapping(prev => ({...prev, notes: v}))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Sélectionner..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">-- Non mappé --</SelectItem>
                          {availableColumns.map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                
                {/* Preview table */}
                {importPreview.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-secondary mb-3">Aperçu des données (5 premières lignes)</h3>
                    <div className="overflow-x-auto border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            {availableColumns.slice(0, 6).map(col => (
                              <TableHead key={col} className="text-xs whitespace-nowrap">{col}</TableHead>
                            ))}
                            {availableColumns.length > 6 && (
                              <TableHead className="text-xs">+{availableColumns.length - 6} cols</TableHead>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importPreview.map((row, idx) => (
                            <TableRow key={idx}>
                              {availableColumns.slice(0, 6).map(col => (
                                <TableCell key={col} className="text-xs truncate max-w-[150px]">
                                  {String(row[col] || "").substring(0, 30)}
                                </TableCell>
                              ))}
                              {availableColumns.length > 6 && (
                                <TableCell className="text-xs text-muted-foreground">...</TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
                
                {/* Warning */}
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-800">Gestion des doublons</p>
                    <p className="text-amber-700 mt-1">
                      Les leads avec un email déjà existant ou une combinaison nom+ville identique seront ignorés.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportDialog(false); resetImport(); }}>
              Annuler
            </Button>
            {importFile && (
              <Button 
                onClick={handleImport} 
                disabled={importing || !importMapping.nom_entreprise}
                className="bg-green-600 hover:bg-green-700"
              >
                {importing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Import en cours...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Importer les leads</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup/Restore Dialog */}
      <Dialog open={showBackupDialog} onOpenChange={setShowBackupDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Sauvegarde & Restauration CRM
            </DialogTitle>
            <DialogDescription>
              Créez des backups de vos leads et clients, ou restaurez depuis un backup précédent.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Create Backup Section */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-green-800 flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Créer un backup
                  </h4>
                  <p className="text-sm text-green-700 mt-1">
                    Sauvegardez tous vos {clients.length} clients/leads actuels
                  </p>
                </div>
                <Button 
                  onClick={handleCreateBackup}
                  disabled={creatingBackup}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {creatingBackup ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Création...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Sauvegarder maintenant</>
                  )}
                </Button>
              </div>
            </div>

            {/* Backups List */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-secondary flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Backups disponibles
                </h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={loadBackups}
                  disabled={loadingBackups}
                >
                  <RefreshCw className={`w-4 h-4 ${loadingBackups ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {loadingBackups ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : backups.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed">
                  <Database className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                  <p className="text-muted-foreground">Aucun backup disponible</p>
                  <p className="text-sm text-muted-foreground">Créez votre premier backup ci-dessus</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {backups.map((backup) => (
                    <div 
                      key={backup.id} 
                      className={`p-3 rounded-lg border ${backup.is_safety_backup ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">
                              {format(parseISO(backup.created_at), "dd MMM yyyy à HH:mm", { locale: fr })}
                            </p>
                            {backup.is_safety_backup && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs">Auto (pré-restore)</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {backup.total_clients} clients/leads • Par {backup.created_by_email?.split('@')[0] || 'système'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadBackup(backup.id)}
                            title="Télécharger le backup"
                          >
                            <Download className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleRestoreBackup(backup.id, format(parseISO(backup.created_at), "dd/MM/yyyy HH:mm"))}
                            disabled={restoringBackup}
                            className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          >
                            {restoringBackup ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><RotateCcw className="w-4 h-4 mr-1" /> Restaurer</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Warning */}
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-amber-800">Important</p>
                <p className="text-amber-700 mt-1">
                  La restauration remplacera <strong>tous</strong> vos clients/leads actuels. 
                  Un backup de sécurité sera automatiquement créé avant la restauration.
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBackupDialog(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Creation Dialog from CRM */}
      <ProposeEventDialog
        isOpen={showEventDialog}
        onOpenChange={(open) => {
          setShowEventDialog(open);
          if (!open) setSelectedClientForEvent(null);
        }}
        token={localStorage.getItem("token")}
        onEventProposed={handleEventCreatedFromCRM}
        initialData={selectedClientForEvent}
      />

      {/* New Quote Dialog (without existing client) */}
      <Dialog open={showNewQuoteDialog} onOpenChange={setShowNewQuoteDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <Euro className="w-5 h-5" />
              Nouveau Devis
            </DialogTitle>
            <DialogDescription>
              Créez un devis pour un nouveau client
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Client Info */}
            <div className="space-y-4">
              <Label className="text-secondary font-semibold">Informations Client</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="new_quote_entreprise">Nom de l'entreprise *</Label>
                  <Input
                    id="new_quote_entreprise"
                    value={newQuoteForm.nom_entreprise}
                    onChange={(e) => setNewQuoteForm({ ...newQuoteForm, nom_entreprise: e.target.value })}
                    placeholder="Nom de l'entreprise"
                  />
                </div>
                <div>
                  <Label htmlFor="new_quote_contact">Contact</Label>
                  <Input
                    id="new_quote_contact"
                    value={newQuoteForm.nom_contact}
                    onChange={(e) => setNewQuoteForm({ ...newQuoteForm, nom_contact: e.target.value })}
                    placeholder="Nom du contact"
                  />
                </div>
                <div>
                  <Label htmlFor="new_quote_email">Email</Label>
                  <Input
                    id="new_quote_email"
                    type="email"
                    value={newQuoteForm.email}
                    onChange={(e) => setNewQuoteForm({ ...newQuoteForm, email: e.target.value })}
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <Label htmlFor="new_quote_telephone">Téléphone</Label>
                  <Input
                    id="new_quote_telephone"
                    value={newQuoteForm.telephone}
                    onChange={(e) => setNewQuoteForm({ ...newQuoteForm, telephone: e.target.value })}
                    placeholder="01 23 45 67 89"
                  />
                </div>
                <div>
                  <Label htmlFor="new_quote_ville">Ville</Label>
                  <Input
                    id="new_quote_ville"
                    value={newQuoteForm.ville}
                    onChange={(e) => setNewQuoteForm({ ...newQuoteForm, ville: e.target.value })}
                    placeholder="Paris"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="new_quote_adresse">Adresse</Label>
                  <Input
                    id="new_quote_adresse"
                    value={newQuoteForm.adresse}
                    onChange={(e) => setNewQuoteForm({ ...newQuoteForm, adresse: e.target.value })}
                    placeholder="Adresse complète"
                  />
                </div>
              </div>
            </div>

            {/* Prestations */}
            <div>
              <Label className="text-secondary font-semibold mb-3 block">Prestations</Label>
              <div className="space-y-3">
                {newQuoteForm.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border border-[#E5E0DC] rounded-lg">
                    <Input
                      placeholder="Description de la prestation"
                      value={item.description}
                      onChange={(e) => updateNewQuoteItem(index, 'description', e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Qté"
                      value={item.quantite}
                      onChange={(e) => updateNewQuoteItem(index, 'quantite', parseInt(e.target.value) || 1)}
                      className="w-20"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Prix €"
                      value={item.prix_unitaire || ''}
                      onChange={(e) => updateNewQuoteItem(index, 'prix_unitaire', parseFloat(e.target.value) || 0)}
                      className="w-28"
                    />
                    {newQuoteForm.items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeNewQuoteItem(index)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addNewQuoteItem}
                  className="w-full border-dashed"
                >
                  <Plus className="w-4 h-4 mr-1" /> Ajouter une prestation
                </Button>
              </div>
            </div>

            {/* TVA */}
            <div>
              <Label className="text-secondary font-semibold">TVA</Label>
              <Select
                value={newQuoteForm.tva_rate.toString()}
                onValueChange={(v) => setNewQuoteForm({ ...newQuoteForm, tva_rate: parseFloat(v) })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">TVA non applicable (0%)</SelectItem>
                  <SelectItem value="10">TVA 10%</SelectItem>
                  <SelectItem value="20">TVA 20%</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-secondary font-semibold">Notes (optionnel)</Label>
              <Textarea
                value={newQuoteForm.notes}
                onChange={(e) => setNewQuoteForm({ ...newQuoteForm, notes: e.target.value })}
                placeholder="Conditions particulières, informations complémentaires..."
                rows={2}
              />
            </div>

            {/* Total */}
            <div className="p-4 bg-[#F9F7F5] rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Total HT</span>
                <span className="font-medium">{calculateNewQuoteTotal().totalHT.toFixed(2)} €</span>
              </div>
              {newQuoteForm.tva_rate > 0 && (
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>TVA ({newQuoteForm.tva_rate}%)</span>
                  <span>{calculateNewQuoteTotal().tva.toFixed(2)} €</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-primary mt-2 pt-2 border-t">
                <span>Total TTC</span>
                <span>{calculateNewQuoteTotal().totalTTC.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowNewQuoteDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleGenerateNewQuote}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Créer le devis
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
